import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostUserAlbumsResult,
  PostUserAlbumsOptions,
  PatchUserAlbumsResult,
  PatchUserAlbumsOptions,
  GetUserAlbumsOptions,
  GetUserAlbumsResult,
  getUserAlbums,
  PostUserAlbumsFields,
  postUserAlbums,
  PatchUserAlbumsFields,
  patchUserAlbums,
  GetUserAlbumsFields,
} from '../fetchers/user/albums';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetUserAlbumsOptions<Fields extends GetUserAlbumsFields> = GetUserAlbumsOptions<Fields>;

/**
 * Get all the showcases that belong to the user
 * This method returns every showcase belonging to the authenticated user.
 */

export function useGetUserAlbums<Fields extends GetUserAlbumsFields>(
  getOptions: UseGetUserAlbumsOptions<Fields> | (() => UseGetUserAlbumsOptions<Fields> | null),
  config?: SWRConfiguration<GetUserAlbumsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/users/${options.where.userId}/albums${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getUserAlbums({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetUserAlbums, {
    endpoint: '/users/:userId/albums',
    method: 'GET',
  });
}

/**
 * Get all the showcases that belong to the user
 * This method returns every showcase belonging to the authenticated user.
 */

export function useGetUserAlbumsLazy<Fields extends GetUserAlbumsFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetUserAlbumsResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetUserAlbumsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/users/${options.where.userId}/albums${serializeQuery(options)}`,
          getUserAlbums({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetUserAlbumsLazy, {
    endpoint: '/users/:userId/albums',
    method: 'GET',
  });
}

type UseGetUserAlbumsOptionsInfinite<Fields extends GetUserAlbumsFields> =
  GetUserAlbumsOptions<Fields>;

/**
 * Get all the showcases that belong to the user
 * This method returns every showcase belonging to the authenticated user.
 */

export function useGetUserAlbumsInfinite<Fields extends GetUserAlbumsFields>(
  getOptions:
    | UseGetUserAlbumsOptionsInfinite<Fields>
    | (() => UseGetUserAlbumsOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetUserAlbumsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (pageIndex: number, previousPageData: GetUserAlbumsResult<Fields> | null) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/users/${options.where.userId}/albums?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getUserAlbums({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetUserAlbumsInfinite, {
    endpoint: '/users/:userId/albums',
    method: 'GET',
  });
}

type UsePostUserAlbumsOptions<Fields extends PostUserAlbumsFields> = PostUserAlbumsOptions<Fields>;

/**
 * Create a showcase
 * This method creates a new showcase for the specified user.
 */

export function usePostUserAlbums<Fields extends PostUserAlbumsFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PostUserAlbumsResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePostUserAlbumsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await postUserAlbums({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostUserAlbums, {
    endpoint: '/users/:userId/albums',
    method: 'POST',
  });
}

type UsePatchUserAlbumsOptions<Fields extends PatchUserAlbumsFields> =
  PatchUserAlbumsOptions<Fields>;

/**
 * Add videos and events to showcases
 * This method adds videos and events to the specified showcases. The authenticated user must either be the owner of the showcase or have team permissions.
 *
 * The present setup permits only one event per showcase.
 */

export function usePatchUserAlbums<Fields extends PatchUserAlbumsFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PatchUserAlbumsResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePatchUserAlbumsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await patchUserAlbums({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchUserAlbums, {
    endpoint: '/users/:userId/albums',
    method: 'PATCH',
  });
}
